import { useState } from "react";
import { Link } from "react-router-dom";
import { useMe } from "../App";
import { useApi } from "../lib/api";
import { Badge, Card, cx, ErrorNote, Loading, pct, Stat, Table, Td } from "../components/ui";

export default function Stats() {
  const { me } = useMe();
  const [mine, setMine] = useState(!me.isAdmin);
  const { data, error, loading } = useApi<any>(`/stats${mine ? "?mine=1" : ""}`, [mine]);
  if (loading && !data) return <Loading />;
  if (!data) return <ErrorNote error={error} />;
  const t = data.totals;
  const mins = (s: number) => (s < 60 ? `${s}s` : `${Math.round(s / 60)} min`);
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-[1.45rem] font-semibold tracking-tight">Campaign Stats</h1>
        {me.isAdmin && (
          <label className="flex items-center gap-2 text-sm text-muted"><input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} /> Only mine</label>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-8">
        <Stat label="Enrolled" value={t.enrolled} />
        <Stat label="Active" value={t.active} />
        <Stat label="Completed" value={t.completed} />
        <Stat label="Replied" value={t.replied} />
        <Stat label="Bounced" value={t.bounced} tone={t.bounced ? "bad" : undefined} />
        <Stat label="Reply rate" value={pct(t.replyRate)} sub={`of ${t.emailed} emailed`} />
        <Stat label="Completion rate" value={pct(t.completionRate)} sub="target 90%+" tone={t.completionRate >= 0.9 ? "good" : "warn"} />
        <Stat label="Avg edit time / sequence" value={mins(t.avgEditSeconds)} sub="should trend down" />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Per rep">
          <Table head={["Rep", "Enrolled (7 days)", "Open tasks", "Overdue"]}>
            {data.perRep.map((r: any) => (
              <tr key={r.sub}>
                <Td>{r.name}</Td>
                <Td className="tabular-nums">{r.enrolled} ({r.enrolledThisWeek})</Td>
                <Td className="tabular-nums">{r.openTasks}</Td>
                <Td className={cx("tabular-nums", r.overdueTasks && "font-semibold text-bad")}>{r.overdueTasks}</Td>
              </tr>
            ))}
          </Table>
        </Card>
        <Card title="Edit time by week">
          {!data.editTimeByWeek.length ? <div className="text-sm text-muted">No approvals yet.</div> : (
            <div className="space-y-1">
              {data.editTimeByWeek.map((w: any) => {
                const max = Math.max(...data.editTimeByWeek.map((x: any) => x.avgSeconds), 1);
                return (
                  <div key={w.week} className="flex items-center gap-2 text-xs">
                    <span className="w-20 text-muted">{w.week}</span>
                    <div className="h-3 flex-1 rounded bg-surface-2"><div className="h-3 rounded bg-accent" style={{ width: `${(w.avgSeconds / max) * 100}%` }} /></div>
                    <span className="w-24 text-right tabular-nums">{mins(w.avgSeconds)} · {w.sequences}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Group title="Reply rate by offering" g={data.byOffering} />
        <Group title="Reply rate by play tier" g={data.byTier} />
      </div>
      <div className="text-xs text-muted">Meetings booked come from Salesforce outcomes (within 60 days of the final touch) and appear once the Salesforce integration is connected (Phase 4). "Interested" replies are shown as the leading indicator meanwhile.</div>
      <Card title="Campaigns">
        <Table head={["Campaign", "Offering", "Rep", "Enrolled", "Emailed", "Replied", "Reply rate", "Completion"]}>
          {data.campaigns.map((c: any) => (
            <tr key={c.id}>
              <Td><Link className="text-accent-text hover:underline" to={`/campaigns/${c.id}`}>{c.name}</Link></Td>
              <Td>{c.offering}</Td>
              <Td>{c.ownerName}</Td>
              <Td className="tabular-nums">{c.enrolled}</Td>
              <Td className="tabular-nums">{c.emailed}</Td>
              <Td className="tabular-nums">{c.replied}</Td>
              <Td className="tabular-nums">{pct(c.replyRate)}</Td>
              <Td className={cx("tabular-nums", c.completionRate >= 0.9 ? "text-good" : "text-warn")}>{pct(c.completionRate)}</Td>
            </tr>
          ))}
        </Table>
      </Card>
      <Card title="Mailboxes">
        <Table head={["Rep", "Mailbox", "Mode", "Sent today", "Bounce rate", ""]}>
          {data.mailboxes.map((m: any) => (
            <tr key={m.repSub}>
              <Td>{m.repName}</Td>
              <Td>{m.address}</Td>
              <Td><Badge tone={m.mode}>{m.mode}</Badge></Td>
              <Td><CapBar n={m.sentToday} cap={m.capToday} /></Td>
              <Td><BounceRate r={m.bounceRate} /></Td>
              <Td>{m.paused && <Badge tone="failed">paused</Badge>}</Td>
            </tr>
          ))}
        </Table>
      </Card>
    </div>
  );
}

function Group({ title, g }: { title: string; g: Record<string, any> }) {
  const rows = Object.entries(g).sort((a, b) => b[1].enrolled - a[1].enrolled);
  return (
    <Card title={title}>
      <Table head={["", "Enrolled", "Emailed", "Replied", "Interested", "Reply rate"]}>
        {rows.map(([k, v]) => (
          <tr key={k}><Td>{k}</Td><Td className="tabular-nums">{v.enrolled}</Td><Td className="tabular-nums">{v.emailed}</Td><Td className="tabular-nums">{v.replied}</Td><Td className="tabular-nums">{v.interested}</Td><Td className="tabular-nums">{pct(v.emailed ? v.replied / v.emailed : 0)}</Td></tr>
        ))}
      </Table>
    </Card>
  );
}

export function CapBar({ n, cap = 50 }: { n: number; cap?: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 rounded bg-surface-2"><div className={cx("h-2 rounded", n >= cap ? "bg-bad-soft0" : "bg-accent")} style={{ width: `${Math.min(100, (n / cap) * 100)}%` }} /></div>
      <span className="text-xs tabular-nums">{n}/{cap}</span>
    </div>
  );
}
export function BounceRate({ r }: { r: number }) {
  return <span className={cx("text-sm font-medium tabular-nums", r < 0.03 ? "text-good" : r <= 0.05 ? "text-warn" : "text-bad")}>{(r * 100).toFixed(1)}%</span>;
}
